import { Alert, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  filterToCondition,
  ResourceView,
  type ActiveFiltersState,
  type Filter,
  type FilterMatchMode,
  type FilterMenuOutput,
} from "@/shared/components/ResourceView";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { type DashboardRecord } from "@/shared/domain/dashboards";
import {
  dashboardQueryKeys,
  useCreateDashboard,
  useDashboard,
  useDeleteDashboard,
} from "@/shared/domain/dashboards/hooks";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Period } from "@/shared/domain/metrics";
import { useAllMetricDefinitions } from "@/shared/domain/metrics/hooks";
import { Button } from "@/shared/foundation/primitives";
import { type Updater } from "@/shared/foundation/primitives/Table";
import { RobotoConflictException, RobotoDomainException } from "@/shared/http";
import { Goto } from "@/shared/navigation/Goto";

import { DashboardConflictDialog } from "../dashboardConflict/DashboardConflictDialog";
import {
  clearDraft,
  draftKeyFor,
  loadDraft,
  pruneScopeDrafts,
  saveDraft,
  sweepExpiredDrafts,
} from "../dashboardDrafts/dashboardDraft";
import { DashboardSwitchGuard } from "../dashboardDrafts/DashboardSwitchGuard";
import { planDraftSeed } from "../dashboardDrafts/draftSeed";
import {
  buildDashboardCustomFieldFilterDefinitions,
  DASHBOARD_PROPERTY_FILTERS,
  sortByFieldToken,
} from "../dashboardFilters/dashboardPropertyFilters";
import { groupableFieldDefinitions } from "../dashboardFilters/groupableFields";
import { DashboardHeader } from "../DashboardHeader/DashboardHeader";
import { DashboardSelectReason } from "../DashboardHeader/DashboardMenu";
import {
  appendFilter,
  applyDefinitionParams,
  buildDashboardDefinition,
  CardConfig,
  CardKind,
  clearDashboardParams,
  clearFilters,
  DASHBOARD_PARAMS,
  DashboardDefinition,
  DashboardLayout,
  definitionsDifferInLayout,
  effectiveChartMetric,
  effectiveTrendMetric,
  emptyDashboardLayout,
  fallbackSections,
  generateCardId,
  isDashboardDefinition,
  mergeParams,
  normalizeDashboardId,
  normalizeDefinition,
  Placement,
  periodFitsRange,
  periodForRange,
  rangeForPeriod,
  readFilterState,
  readPeriod,
  readRange,
  removeFilterAt,
  setFilterMatchMode,
  TimeRangeMode,
  updateFilterAt,
  writeRange,
} from "../DashboardHeader/DashboardParams";
import { useSaveDashboardDefinition } from "../dashboardSave/useSaveDashboardDefinition";
import {
  DashboardSections,
  LayoutChange,
} from "../DashboardSections/DashboardSections";
import { resolveAxisDomain } from "../MetricChartCard/axisDomain";
import { MetricChartCard } from "../MetricChartCard/MetricChartCard";
import { MetricsOnboarding } from "../MetricsOnboarding/MetricsOnboarding";
import { TrendTile } from "../TrendTile/TrendTile";
import { WidgetEditor } from "../WidgetEditor";
import {
  clearWidgetDraft,
  defaultWidgetDraft,
  NEW_WIDGET_ID,
  readWidgetDraft,
  useWidgetEditorMode,
  writeWidgetDraft,
} from "../WidgetEditor/widgetEditorMode";

import {
  clearRememberedDashboardId,
  loadRememberedDashboardId,
  rememberDashboardId,
} from "./dashboardSelection";
import styles from "./MetricsDashboard.module.css";
import { defaultDashboardLayout } from "./sampleDashboard";
import {
  type ExplicitDashboardSelection,
  useInitialDashboardSelection,
} from "./useInitialDashboardSelection";

/** Matches the app's error-toast duration for dialog-less mutation failures
 * (see DashboardMenu's header-Save). */
const ERROR_NOTIFICATION_DURATION_MS = 6000;

/** Placeholder names an auto-create tries before it shows an error. */
const MAX_AUTO_NAME_ATTEMPTS = 3;

/** Longest metric name that a placeholder name keeps in full. */
const MAX_METRIC_LENGTH = 12;

/**
 * Placeholder name for an auto-created dashboard, for example
 * `Untitled-cpu.usage-k3x9qa`. A metric name longer than 12 characters becomes
 * its first 6 and last 6 characters around `…`. An empty `metricName` gives
 * `Untitled-<id>`. The id is 6 random base36 characters, because names must be
 * unique for each owner.
 */
function generateUntitledDashboardName(metricName: string): string {
  const id = Math.random().toString(36).slice(2, 8);
  if (metricName === "") {
    return `Untitled-${id}`;
  }
  const shortName =
    metricName.length <= MAX_METRIC_LENGTH
      ? metricName
      : `${metricName.slice(0, 6)}…${metricName.slice(-6)}`;
  return `Untitled-${shortName}-${id}`;
}

/**
 * String message for `showNotification`, which only accepts strings. A backend
 * 4XX message is server-authored and safe to show verbatim; anything else gets
 * the operation-specific fallback. (Twin of DashboardMenu's helper.)
 */
function toastErrorMessage(error: unknown, fallback: string): string {
  return error instanceof RobotoDomainException && error.isClientError()
    ? error.message
    : fallback;
}

export interface MetricsDashboardProps {
  orgId: string | undefined;
  /** Restrict every query to one device. Top-level dashboard omits this. */
  deviceId?: string;
  /** Restrict every query to one session. Top-level dashboard omits this. */
  sessionId?: string;
}

/**
 * Org-, device-, or session-scoped metrics dashboard. The same component
 * powers (a) the top-level metrics route (no filter), (b) the device-detail
 * "Metrics" tab (deviceId set), and (c) any session-scoped view (sessionId
 * set).
 *
 * Layout, top to bottom: global controls (filter, time range, period) → the
 * chips for whatever filters are applied → the placed card grid
 * ({@link DashboardSections}): titled sections, each a 6-column canvas where
 * cards (trend rows and charts) carry explicit placements and can be dragged,
 * resized, added at any empty cell, and removed.
 *
 * View-state ownership: the active dashboard id is the `:dashboardId?` path
 * param, and the `range`/`period`/`filters` query params are OVERRIDES on the
 * loaded dashboard's saved values — a bare URL shows the dashboard as saved, a
 * param deviates from it, and refresh/shared links keep the deviation because
 * the URL keeps it. Switching dashboards clears the overrides so the next
 * record starts from its own saved state. The layout itself (sections +
 * cards) is component state. Every layout edit snapshots the full definition
 * into a per-context localStorage draft (`dashboardDraft.ts`) so unsaved edits
 * survive reload, and Save persists it as a versioned `dashboard_definition`
 * on the backend record. When the org has no metric definitions the whole
 * thing is replaced by an onboarding empty state.
 *
 * This component never renders the metric-definitions *management* table —
 * that lives behind the admin-only "Metric Definitions" tab in
 * `MetricsContent`.
 */
export function MetricsDashboard({
  orgId,
  deviceId,
  sessionId,
}: MetricsDashboardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { dashboardId: dashboardIdParam } = useParams<{
    dashboardId?: string;
  }>();
  // The layout (sections + placed cards) as component state — never URL
  // params, so layout edits cause no URL churn. (The initial value is never
  // painted: the seeding effect below replaces it before the canvas renders.)
  const [layout, setLayout] = useState<DashboardLayout>(emptyDashboardLayout);
  const { sections, cards } = layout;

  const dashboardBodyRef = useRef<HTMLDivElement>(null);

  const editorMode = useWidgetEditorMode({
    deviceScoped: deviceId !== undefined,
  });

  // Normalized at the read site: an absent or empty/whitespace `:dashboardId?`
  // path segment is `null` here, so the render gate, `useDashboard`, and draft
  // keys all agree on what "no dashboard selected" means.
  const activeDashboardId = normalizeDashboardId(dashboardIdParam);
  const dashboardQuery = useDashboard(activeDashboardId);
  const loadedDashboard = dashboardQuery.data ?? null;

  // A loaded record whose stored definition is not a valid current-schema
  // layout must never seed (or masquerade as) the dashboard. Like a hard-failed
  // fetch, it can never yield a usable layout, so it feeds the same failure
  // signal below rather than dead-ending the page.
  const loadedDefinitionInvalid =
    loadedDashboard !== null &&
    loadedDashboard.dashboard_id === activeDashboardId &&
    !isDashboardDefinition(loadedDashboard.dashboard_definition);

  // A selected dashboard that can never load — its fetch hard-failed (e.g. a
  // non-existent id that 404s) or its stored definition is invalid — must not
  // dead-end the whole page. We treat it as if no dashboard is effectively
  // active: the default/empty view seeds and renders normally (under an error
  // banner surfaced below), and — crucially — its draft keys under the
  // UNSAVED-view context, so edits are never persisted under the failed
  // dashboard's key. The picker stays present as the recovery affordance.
  const dashboardLoadFailed =
    activeDashboardId !== null &&
    (dashboardQuery.isError || loadedDefinitionInvalid);
  const effectiveDashboardId = dashboardLoadFailed ? null : activeDashboardId;

  // The loaded record's normalized definition — what a BARE URL means. The URL
  // is an override layer on top of it: a param present deviates from the saved
  // view, a param absent reads as the saved value. Null for the unsaved view
  // (and a failed load, which renders the default view), where absence means
  // the static defaults instead.
  const savedBase = useMemo(
    () =>
      effectiveDashboardId !== null &&
      loadedDashboard !== null &&
      loadedDashboard.dashboard_id === effectiveDashboardId &&
      isDashboardDefinition(loadedDashboard.dashboard_definition)
        ? normalizeDefinition(loadedDashboard.dashboard_definition)
        : null,
    [effectiveDashboardId, loadedDashboard],
  );

  // Memoized, not called inline: `readRange` builds a fresh object for a
  // custom range, so an inline call hands every dependent a new identity each
  // render — including the resolution below, which would then re-read the
  // clock every render. `useSearchParams` memoizes on `location.search`, so
  // this recomputes exactly when the URL or the loaded record changes.
  const mode = useMemo(
    () => readRange(searchParams, savedBase?.range),
    [searchParams, savedBase],
  );
  const period = readPeriod(searchParams, mode, savedBase?.period);

  // The only place the clock is read. Resolving here rather than per card is
  // what guarantees every card shows the same window: a card resolving its own
  // `now` would drift from its siblings after any remount.
  const range = useMemo(() => TimeRange.fromMode(mode), [mode]);

  // Resolved here for the same reason as `range`: the window every chart reads
  // and draws is a property of the DASHBOARD, not of a card. Each card used to
  // end its axis wherever its own metric last reported, so two cards over one
  // window could disagree about what was on screen.
  //
  // `period` is an input because the bounds are snapped to its calendar
  // boundaries — the same snapping `aggregate_metric` applies before it groups.
  // That makes this the window the server actually reads, which is what lets a
  // raw card and an aggregated one be handed the same one.
  const axisDomain = useMemo(
    () => resolveAxisDomain(range, mode, period),
    [range, mode, period],
  );

  const {
    data: definitions,
    isLoading,
    error,
  } = useAllMetricDefinitions(orgId);

  // Which layout an UNSAVED view seeds. "empty" (one bare section, no cards)
  // is the default — "New empty dashboard", a resumed unsaved draft, and the
  // failed-load fallback all seed it. "sample" (the full sample-dashboard
  // seed) comes from either of the two paths that ask for it: the picker's
  // "New sample dashboard" row, or a priority-3 landing outcome (the org has
  // metrics but not a single visible dashboard).
  const [unsavedSeed, setUnsavedSeed] = useState<"empty" | "sample">("empty");

  // The user's explicit in-session choice, readable synchronously by the
  // landing-selection hook at navigation time: `unsaved-view` marks a bare
  // route the user chose on purpose (either "New … dashboard" row, the
  // post-delete fallback), which re-entry must not re-resolve away from.
  // Written by the menu/save handlers below.
  const explicitSelectionRef = useRef<ExplicitDashboardSelection>({
    kind: "none",
  });

  // Landing-time selection: on each navigation onto a bare route, pick the
  // remembered/default dashboard (navigating to it with `replace`), the
  // unsaved draft, or the sample seed — see useInitialDashboardSelection. While
  // it decides, the render below shows a spinner so the wrong canvas is
  // never painted.
  const { deciding: selectionDeciding } = useInitialDashboardSelection({
    orgId,
    deviceId,
    activeDashboardId,
    // The heuristic serves the two routed mounts (org page, device tab).
    enabled: sessionId === undefined,
    explicitSelectionRef,
    definitionsCount: definitions?.length,
    onSample: () => setUnsavedSeed("sample"),
  });

  const draftKey = draftKeyFor({
    orgId,
    deviceId,
    sessionId,
    dashboardId: effectiveDashboardId,
  });

  // Once per mount, age out abandoned drafts everywhere (all orgs/scopes) —
  // the lazy per-key purge in loadDraft only ever touches keys the user
  // revisits.
  useEffect(() => {
    sweepExpiredDrafts();
  }, []);

  // Whether the current context has a stored draft.
  //
  // This is what "unsaved edits" MEANS for an unsaved view, which has no
  // record to be dirty against. Note it cannot be a comparison against the
  // seeded layout: a draft auto-loads, so the canvas matches it exactly on
  // arrival — yet those edits are still unsaved, still the scope's single
  // working copy, and still what a switch would put at risk.
  const [contextHasDraft, setContextHasDraft] = useState(false);

  /** Clear a draft and keep {@link contextHasDraft} honest. Every clear goes
   *  through here so the flag can never drift from storage. */
  const dropDraft = (key: string) => {
    clearDraft(key);
    setContextHasDraft(false);
  };

  // DashboardMenu's Save-as dialog. Its opener, the switch prompt, re-seeds the
  // canvas. That remounts the menu, so the menu cannot hold this state.
  const [saveAsOpen, setSaveAsOpen] = useState(false);

  // A dashboard switch held back by unsaved layout edits, awaiting the user's
  // answer in DashboardSwitchGuard. Keyed by the draft context it was raised
  // in, exactly as the seeding state is, so a context change can never apply a
  // stale switch.
  const [pendingSwitch, setPendingSwitch] = useState<{
    key: string;
    nextId: string | null;
    meta: { reason: DashboardSelectReason; seed?: "empty" | "sample" };
  } | null>(null);

  // The revision the canvas was seeded from — the "this is what I loaded" half
  // of the save contract, held for the whole editing session.
  //
  // Deliberately NOT read from a query cache at save time. `useDashboards` polls
  // every 60s and `useDashboard` inherits the global 5-minute staleTime with
  // refetchOnWindowFocus enabled, so a cache read at Save would quietly adopt a
  // concurrent writer's revision and wave through the very save the guard exists
  // to stop. Keyed like the seeding state so a capture can only apply to the
  // context it was made for.
  const [baseRevision, setBaseRevision] = useState<{
    key: string;
    revision: number;
  } | null>(null);
  const effectiveBaseRevision =
    baseRevision?.key === draftKey ? baseRevision.revision : null;

  // A save was rejected because someone else changed the dashboard first. Held
  // here rather than in the save surfaces because both resolutions act on state
  // this component owns — the layout, the URL params, the draft, the base
  // revision, and the dashboard selection. `fromSwitchGuard` records which
  // surface raised it, so a conflict raised out of a held dashboard switch can
  // release that switch once it is resolved.
  const [conflict, setConflict] = useState<{
    serverRecord: DashboardRecord;
    fromSwitchGuard: boolean;
  } | null>(null);

  const queryClient = useQueryClient();

  const createDashboard = useCreateDashboard();
  // True from the first auto-create attempt until the last one ends. The gap
  // between two attempts is not pending, so `isPending` does not cover it.
  const [isAutoCreating, setIsAutoCreating] = useState(false);

  // Seed the layout once per context (org / scope / dashboard id). A stored
  // draft that holds real layout edits IS the seed — it is the user's working
  // copy, applied without asking; one that normalizes to the baseline holds no
  // edit and is dropped. Otherwise the baseline seeds: the loaded record, or
  // the default layout for an unsaved view. The seed's range and period
  // hydrate into the URL; from then on state (layout) and URL (range/period)
  // are the live sources of truth during editing.
  const seededForKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededForKeyRef.current === draftKey) {
      return;
    }
    const draft = loadDraft(draftKey);
    // `revision` is the record's revision for a saved dashboard, or null for an
    // unsaved view (whose save path is a create, which cannot conflict).
    // Seeding never writes a RECORD's params to the URL — a bare URL already
    // means "as saved" (the read fallbacks), and whatever params the URL
    // carries are the user's overrides, which a load must not disturb. Only an
    // APPLIED DRAFT writes: its params are the user's own working copy, as
    // much a part of it as the cards are, so they materialize as overrides.
    const seedBaseline = (
      baseline: DashboardDefinition,
      revision: number | null,
    ) => {
      seededForKeyRef.current = draftKey;
      setPendingSwitch(null);
      setBaseRevision(revision === null ? null : { key: draftKey, revision });
      const plan = planDraftSeed(draft, baseline);
      if (plan.kind === "drop-draft") {
        // `clearDraft` rather than `dropDraft`: the flag is set outright on
        // the next line, and the module function keeps this effect free of a
        // dependency on a per-render closure.
        clearDraft(draftKey);
      }
      setContextHasDraft(plan.kind === "apply-draft");
      // One setLayout, from whichever definition wins — seeding the baseline
      // and then overwriting it would flash the saved layout on every load of
      // an edited dashboard.
      const effective = plan.kind === "apply-draft" ? plan.draft : baseline;
      setLayout({ sections: effective.sections, cards: effective.cards });
      if (plan.kind === "apply-draft") {
        // Auto-loading is now the only way a draft is ever touched, so this
        // is what keeps a working copy you keep coming back to from aging out
        // from under you at DRAFT_TTL_MS.
        saveDraft(draftKey, effective);
        setSearchParams((prev) => applyDefinitionParams(prev, effective), {
          replace: true,
        });
      }
    };
    if (effectiveDashboardId === null) {
      // Unsaved view — or a hard-failed load treated as one. While the
      // landing selection is still deciding, don't seed: the outcome may be
      // a navigation to a saved dashboard, or the sample seed kind.
      if (selectionDeciding) {
        return;
      }
      // The baseline is the seed layout for this unsaved view — the sample
      // dashboard when `unsavedSeed` says so (a priority-3 landing; the
      // picker's sample row seeds itself, see `commitDashboardSwitch`), the
      // empty layout everywhere else — under the URL's current range/period.
      const seed =
        unsavedSeed === "sample"
          ? defaultDashboardLayout()
          : emptyDashboardLayout();
      seedBaseline(
        buildDashboardDefinition(searchParams, seed.sections, seed.cards),
        null,
      );
      return;
    }
    if (
      loadedDashboard !== null &&
      loadedDashboard.dashboard_id === effectiveDashboardId &&
      isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      const normalized = normalizeDefinition(
        loadedDashboard.dashboard_definition,
      );
      seedBaseline(normalized, loadedDashboard.revision);
    }
    // Otherwise the record is still loading (the effect re-runs on arrival;
    // a draft for a saved dashboard also waits — prompting needs the record
    // to compare against and to render as the baseline). An invalid stored
    // definition never reaches here: it flips effectiveDashboardId to null
    // above, so the default view seeds under the error banner.
  }, [
    draftKey,
    effectiveDashboardId,
    loadedDashboard,
    searchParams,
    setSearchParams,
    selectionDeciding,
    unsavedSeed,
  ]);

  // True once the current context's baseline (the loaded record, or the
  // default layout for an unsaved view) has seeded the layout state. Until
  // then the layout still holds the PREVIOUS context's (or the initial
  // seed's) content, so it must be neither rendered as this dashboard nor
  // snapshotted into this dashboard's draft. Reading the ref during render
  // is sound here: the seeding effect always pairs the ref write with a
  // setLayout, which re-renders with the updated value.
  const dashboardSeeded = seededForKeyRef.current === draftKey;

  const currentDefinition = useMemo(
    () =>
      buildDashboardDefinition(
        searchParams,
        sections,
        cards,
        savedBase ?? undefined,
      ),
    [searchParams, sections, cards, savedBase],
  );
  // Two tiers of "dirty" against the loaded record:
  //
  // - `isDirty` (SAVEABLE-dirty): ANY difference, range/period included.
  //   Drives the Save affordances and the "(unsaved changes)" marker
  //   (DashboardMenu) — a params-only tweak is still worth persisting.
  // - `isLayoutDirty` (INTERRUPTING-dirty): a sections/cards difference only.
  //   Feeds `hasUnsavedEdits`, and so the prompt raised when picking another
  //   dashboard would replace those edits — a user who only tweaked
  //   range/period is never interrupted.
  const isDirty = useMemo(() => {
    if (
      loadedDashboard === null ||
      !isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      // No loaded record to be dirty against; an invalid stored definition
      // renders as a load error and never seeds, so nothing is comparable.
      return false;
    }
    // Both sides normalize through normalizeDefinition /
    // buildDashboardDefinition, so a stored record loads clean (JSONB key
    // order notwithstanding) and only real edits read as dirty.
    return (
      JSON.stringify(
        normalizeDefinition(loadedDashboard.dashboard_definition),
      ) !== JSON.stringify(currentDefinition)
    );
  }, [loadedDashboard, currentDefinition]);
  const isLayoutDirty = useMemo(() => {
    if (
      !isDirty ||
      loadedDashboard === null ||
      !isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      // Layout-dirty implies saveable-dirty, so a clean view short-circuits.
      return false;
    }
    return definitionsDifferInLayout(
      loadedDashboard.dashboard_definition,
      currentDefinition,
    );
  }, [isDirty, loadedDashboard, currentDefinition]);

  // Whether there are layout edits a dashboard switch would throw away.
  //
  // `isLayoutDirty` cannot answer this alone: it short-circuits to false when
  // no record is loaded, so an UNSAVED view — a new dashboard the user has
  // been building — is never dirty by its measure. For that case the stored
  // draft IS the unsaved work, and it is the scope's single working copy, so
  // losing it silently would be the one real data loss this design could
  // cause.
  const hasUnsavedEdits =
    effectiveDashboardId !== null ? isLayoutDirty : contextHasDraft;

  /**
   * Pick a dashboard, holding the switch behind a prompt when it would throw
   * away unsaved layout edits.
   *
   * The check runs BEFORE `commitDashboardSwitch` does anything, because that
   * function mutates the remembered choice and (for a new dashboard) resets
   * the canvas outright — a switch the user then cancels must leave no trace.
   *
   * `"deleted"` never prompts: the dashboard is gone, so there is nothing to
   * save it to. `"saved"` never prompts either — see the reason's own doc.
   */
  const handleDashboardMenu = (
    nextId: string | null,
    meta: { reason: DashboardSelectReason; seed?: "empty" | "sample" } = {
      reason: nextId === null ? "new" : "select",
    },
  ) => {
    // "new" always counts as a switch, even from the unsaved view where the id
    // does not change (null -> null): asking for a NEW dashboard while one is
    // half-built replaces it, which is the thing worth asking about.
    const replacesTheCanvas =
      meta.reason === "new" || nextId !== effectiveDashboardId;
    if (
      hasUnsavedEdits &&
      replacesTheCanvas &&
      meta.reason !== "deleted" &&
      meta.reason !== "saved"
    ) {
      setPendingSwitch({ key: draftKey, nextId, meta });
      return;
    }
    commitDashboardSwitch(nextId, meta);
  };

  const commitDashboardSwitch = (
    nextId: string | null,
    meta: { reason: DashboardSelectReason; seed?: "empty" | "sample" } = {
      reason: nextId === null ? "new" : "select",
    },
  ) => {
    if (nextId !== null) {
      // A menu load is one of the two explicit-choice recording sites (the
      // other is a successful save). Deep links and the landing heuristic's
      // automatic default never pass through here.
      explicitSelectionRef.current = { kind: "dashboard", dashboardId: nextId };
      if (orgId !== undefined) {
        rememberDashboardId(orgId, nextId);
      }
    } else {
      // Both "New … dashboard" rows (an explicit discard of the
      // unsaved-context draft) and the post-delete fallback reset the canvas
      // to whichever layout `meta.seed` names: the picker's sample row is the
      // one caller that asks for the sample seed, and a delete carries no
      // seed at all, so it lands on the empty layout like the empty row does.
      // Marking the context as seeded keeps the seeding effect from
      // resurrecting anything. None of them records a remembered choice;
      // deleting the currently-remembered dashboard clears the stored id
      // (hygiene — the resolver's staleness fallback would catch it anyway).
      if (
        meta.reason === "deleted" &&
        orgId !== undefined &&
        activeDashboardId !== null &&
        loadRememberedDashboardId(orgId) === activeDashboardId
      ) {
        clearRememberedDashboardId(orgId);
      }
      // The bare route is now the user's own destination — re-entering it
      // must keep this canvas, not re-resolve a landing selection.
      explicitSelectionRef.current = { kind: "unsaved-view" };
      const unsavedKey = draftKeyFor({
        orgId,
        deviceId,
        sessionId,
        dashboardId: null,
      });
      dropDraft(unsavedKey);
      seededForKeyRef.current = unsavedKey;
      const seed = meta.seed ?? "empty";
      setUnsavedSeed(seed);
      // This branch seeds the canvas itself rather than letting the seeding
      // effect do it, so it owns the same bookkeeping — `dropDraft` above is
      // what keeps the new canvas from reading as edited.
      setLayout(
        seed === "sample" ? defaultDashboardLayout() : emptyDashboardLayout(),
      );
    }
    // The dashboard id is a route path segment; swap it while CLEARING the
    // dashboard view-state params — URL params are overrides on the current
    // dashboard, and the next one must start from its own saved state, not
    // inherit this view's range or filters. Unrelated params (e.g. a device
    // page's `tab`) carry over. An explicit dashboard switch is
    // a real navigation, so it PUSHES a history entry (back returns to the
    // previous dashboard) — unlike the range/period controls, which tweak
    // the current view in place and use `replace`, and unlike the landing
    // heuristic's automatic default, which replaces so Back is never trapped
    // on the pre-redirect bare URL.
    void navigate({
      pathname: Goto.createMetricsDashboardRoute({
        deviceId,
        dashboardId: nextId,
      }),
      search: clearDashboardParams(searchParams).toString(),
    });
  };

  // After a successful Save / Save-as the backend record matches the live
  // view, so the draft is no longer an unsaved edit — drop it. (For Save-as
  // this fires before the switch to the new dashboard id, clearing the
  // context the draft was written under.) Saving is also the other
  // explicit-choice recording site: the saved record — the NEW record for
  // Save-as — becomes the remembered dashboard, even when it was reached via
  // a deep link that recorded nothing.
  const handleDashboardSaved = (saved: DashboardRecord) => {
    dropDraft(draftKey);
    explicitSelectionRef.current = {
      kind: "dashboard",
      dashboardId: saved.dashboard_id,
    };
    if (orgId !== undefined) {
      rememberDashboardId(orgId, saved.dashboard_id);
    }
    // The mutation response is the only safe source of the new revision. Key it
    // off the SAVED record's id rather than the ambient draftKey so Save-as
    // lands the capture under the context the app is about to switch into (this
    // fires before onSelect).
    setBaseRevision({
      key: draftKeyFor({
        orgId,
        deviceId,
        sessionId,
        dashboardId: saved.dashboard_id,
      }),
      revision: saved.revision,
    });
  };

  // The header's "restore last saved version" button: discard the live
  // edits — the layout back to the record's, the URL's param overrides
  // deleted (a bare URL IS the saved view) — then clear the draft
  // (the edits it held were just discarded, so nothing may resurrect on the
  // next visit). Only reachable while a dirty saved dashboard is loaded
  // (DashboardMenu gates the button on that), so the guard is
  // belt-and-braces for a stale click during a context switch.
  const handleDashboardRestore = () => {
    if (
      loadedDashboard === null ||
      !isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      return;
    }
    const restored = normalizeDefinition(loadedDashboard.dashboard_definition);
    setLayout({ sections: restored.sections, cards: restored.cards });
    setSearchParams(clearDashboardParams, { replace: true });
    dropDraft(draftKey);
    // The one place reading the detail cache for a revision is correct: the user
    // is explicitly adopting whatever it currently holds as their new baseline,
    // and the definition and revision come from the same record, so they cannot
    // disagree. Everywhere else the base revision must come from the seed or a
    // save response — see the setBaseRevision declaration.
    setBaseRevision({ key: draftKey, revision: loadedDashboard.revision });
  };

  // Conflict resolution 1: adopt the server's version and drop the local edits.
  // Re-seeds from the record carried by the conflict response rather than
  // refetching — that record is the row the server read under its write lock, so
  // its definition and revision are guaranteed mutually consistent, whereas a
  // refetch could land after yet another writer and re-open the same problem one
  // step later.
  const handleConflictDiscardAndReload = (serverRecord: DashboardRecord) => {
    if (isDashboardDefinition(serverRecord.dashboard_definition)) {
      const reloaded = normalizeDefinition(serverRecord.dashboard_definition);
      setLayout({ sections: reloaded.sections, cards: reloaded.cards });
      // Dropping the URL's param overrides is what adopts the server's view
      // state: a bare URL reads as whatever the (just-updated) record says.
      setSearchParams(clearDashboardParams, { replace: true });
    }
    dropDraft(draftKey);
    setBaseRevision({ key: draftKey, revision: serverRecord.revision });
    // Keep the rest of the UI honest about what is now loaded.
    queryClient.setQueryData(
      dashboardQueryKeys.id(serverRecord.dashboard_id, serverRecord.org_id),
      serverRecord,
    );
    setConflict(null);
    // Abandon any held switch. The user asked to throw their edits away and
    // load the server's version — moving them somewhere else on top of that
    // would leave them nowhere near what they just chose to look at.
    setPendingSwitch(null);
  };

  // Conflict resolution 2: the edits were persisted as a new dashboard, which
  // cannot conflict (it is a create). From the header we switch to the copy, as
  // Save-as does; from the switch guard the user had already asked to go
  // somewhere else, so we release that switch rather than redirecting them to
  // the copy.
  const handleConflictSavedCopy = (created: DashboardRecord) => {
    const fromSwitchGuard = conflict?.fromSwitchGuard ?? false;
    const held = pendingSwitch;
    handleDashboardSaved(created);
    setConflict(null);
    if (fromSwitchGuard) {
      showNotification(
        `Your changes were saved as "${created.name}".`,
        "success",
      );
      setPendingSwitch(null);
      if (held !== null && held.key === draftKey) {
        commitDashboardSwitch(held.nextId, held.meta);
      }
      return;
    }
    // The edits are persisted under the new dashboard, so this switch has
    // nothing to prompt about — "saved" says so.
    handleDashboardMenu(created.dashboard_id, { reason: "saved" });
  };

  // Recovery affordance for the invalid-definition banner (below): a real
  // record exists but can never load, so we let the user delete it in place.
  // Only wired up for that case — a hard-failed fetch (404) has no record to
  // delete. On success we drop back to the unsaved/default view exactly as the
  // picker does (`handleDashboardMenu(null)`), which navigates to the bare
  // route (no dashboard path segment) so the banner disappears;
  // `useDeleteDashboard` invalidates the list cache.
  const showNotification = useNotification();
  const deleteMutation = useDeleteDashboard();
  const handleDeleteFailedDashboard = () => {
    if (activeDashboardId === null) {
      return;
    }
    deleteMutation.mutate(activeDashboardId, {
      onSuccess: () => handleDashboardMenu(null, { reason: "deleted" }),
      onError: (deleteError) => {
        // No dialog is on screen, so a failure surfaces as an error toast
        // (the app's pattern for dialog-less mutation failures).
        showNotification(
          toastErrorMessage(
            deleteError,
            "Failed to delete dashboard. Please try again.",
          ),
          "error",
          ERROR_NOTIFICATION_DURATION_MS,
        );
      },
    });
  };

  // Draft snapshots only ever persist AFTER the context has seeded: during a
  // dashboard's load window the layout still shows the previous/seed content,
  // and snapshotting it would store the wrong definition under the loading
  // dashboard's draft key. (The edit surfaces aren't rendered during the load
  // window either — this guards the persistence path itself.)
  // Every draft write goes through here, so this is where the scope is kept
  // down to a single working copy.
  //
  // The prune is a localStorage key scan, and a layout edit fires on every
  // drag/resize tick — so it runs once per context rather than once per write.
  // The ref is compared against `draftKey`, so entering a new context
  // re-arms it without needing a reset.
  const prunedForKeyRef = useRef<string | null>(null);
  const writeDraft = (definition: DashboardDefinition) => {
    if (prunedForKeyRef.current !== draftKey) {
      prunedForKeyRef.current = draftKey;
      pruneScopeDrafts({ orgId, deviceId, sessionId }, draftKey);
    }
    saveDraft(draftKey, definition);
    setContextHasDraft(true);
  };

  const persistDraft = (definition: DashboardDefinition) => {
    if (seededForKeyRef.current !== draftKey) {
      return;
    }
    writeDraft(definition);
  };

  // Params-only counterpart of persistDraft: a range/period tweak never
  // CREATES a draft — it is not a layout edit, its state lives in the URL,
  // and a draft holding nothing else would be dropped un-prompted on the
  // next visit anyway (planDraftSeed compares layout only). But when a
  // draft already exists (a real layout edit is outstanding), the tweak
  // refreshes it, so restoring the draft later brings back the params the
  // user last saw alongside their layout edit.
  const refreshDraftParams = (definition: DashboardDefinition) => {
    if (seededForKeyRef.current !== draftKey) {
      return;
    }
    if (loadDraft(draftKey) === null) {
      return;
    }
    writeDraft(definition);
  };

  // Range and period auto-fit each other, but only when the current pair no
  // longer fits (one too small for the other) — a still-valid combination is
  // left as the user set it. Both live in the URL (replace, so controls don't
  // pollute history); an existing draft is refreshed with them (see
  // refreshDraftParams — a params-only change never creates one).
  const handleRangeChange = (next: TimeRangeMode) => {
    const nextPeriod = periodFitsRange(next, period)
      ? period
      : periodForRange(next);
    const nextParams = mergeParams(searchParams, {
      ...writeRange(next),
      [DASHBOARD_PARAMS.period]: nextPeriod,
    });
    setSearchParams(nextParams, { replace: true });
    refreshDraftParams(
      buildDashboardDefinition(
        nextParams,
        sections,
        cards,
        savedBase ?? undefined,
      ),
    );
  };

  const handlePeriodChange = (next: Period) => {
    const nextRange = periodFitsRange(mode, next) ? mode : rangeForPeriod(next);
    const nextParams = mergeParams(searchParams, {
      [DASHBOARD_PARAMS.period]: next,
      ...writeRange(nextRange),
    });
    setSearchParams(nextParams, { replace: true });
    refreshDraftParams(
      buildDashboardDefinition(
        nextParams,
        sections,
        cards,
        savedBase ?? undefined,
      ),
    );
  };

  // Every layout edit (placement saves, add/remove card, section changes) is
  // one state update — no URL writes — snapshotted to the draft so a single
  // gesture never leaves a half-applied layout behind a reload.
  const handleLayoutChange = (next: LayoutChange) => {
    const nextLayout: DashboardLayout = {
      sections: next.sections ?? sections,
      cards: next.cards ?? cards,
    };
    setLayout(nextLayout);
    persistDraft(
      buildDashboardDefinition(
        searchParams,
        nextLayout.sections,
        nextLayout.cards,
        savedBase ?? undefined,
      ),
    );
  };

  /** The dashboard under the editor, keeping the view params (range, period,
   *  filters). */
  const dashboardLocation = () => ({
    pathname: Goto.createMetricsDashboardRoute({
      deviceId,
      dashboardId: activeDashboardId,
    }),
    search: searchParams.toString(),
  });

  // One id for each new-widget editor session. A retried commit uses the same
  // id, so it replaces its card and does not add a copy.
  const newWidgetIdRef = useRef<string | null>(null);

  /** Open the widget editor on `card`, or on a new widget seeded from the
   *  clicked cell. The view params come along, so the preview renders under
   *  the dashboard's range, period, and filters. */
  const openWidgetEditor = (
    target:
      | { card: CardConfig }
      | { kind: CardKind; section: string; placement: Placement },
  ) => {
    // Reconfiguring starts with no draft params, so the editor reads the card
    // as saved until the first field edit.
    const draftParams =
      "card" in target
        ? clearWidgetDraft()
        : writeWidgetDraft(defaultWidgetDraft(target));
    if ("kind" in target) {
      newWidgetIdRef.current = null;
    }
    void navigate({
      pathname: Goto.createMetricsWidgetRoute({
        deviceId,
        dashboardId: activeDashboardId,
        cardId: "card" in target ? target.card.id : undefined,
      }),
      search: mergeParams(searchParams, draftParams).toString(),
    });
  };

  /** Record a field edit. `replace`, so Back closes the editor rather than
   *  stepping back through each pick. */
  const updateWidgetDraft = (next: CardConfig) => {
    setSearchParams((prev) => mergeParams(prev, writeWidgetDraft(next)), {
      replace: true,
    });
  };

  /** Leave the editor, clearing the draft params and ending its id session. */
  const closeWidgetEditor = () => {
    newWidgetIdRef.current = null;
    void navigate({
      pathname: dashboardLocation().pathname,
      search: mergeParams(searchParams, clearWidgetDraft()).toString(),
    });
  };

  const newWidgetId = () => {
    const id = newWidgetIdRef.current ?? generateCardId();
    newWidgetIdRef.current = id;
    return id;
  };

  /**
   * Put the finished widget on the dashboard, persist it, and return.
   *
   * The write uses the EFFECTIVE layout: the saved record plus pending edits.
   * Thus a commit keeps the moves and renames made before the editor opened.
   * The editor stays open until the write is complete, so the user can retry
   * after a failure.
   */
  const commitWidget = () => {
    if (editorDraft === null) {
      return;
    }
    // Do not save an existing dashboard before its base revision loads. Stop
    // before the layout changes, so a retry does not add a copy. An unsaved
    // view, or a dashboard that failed to load, also has no revision. It goes
    // to the auto-create below.
    if (effectiveDashboardId !== null && effectiveBaseRevision === null) {
      return;
    }
    const next =
      editorMode?.kind === "edit"
        ? editorDraft
        : { ...editorDraft, id: newWidgetId() };
    // Replace a card that has the same id. In create mode, that card is from a
    // failed commit (see `newWidgetId`).
    const nextCards = cards.some((card) => card.id === next.id)
      ? cards.map((card) => (card.id === next.id ? next : card))
      : [...cards, next];
    handleLayoutChange({ cards: nextCards });

    if (effectiveDashboardId === null) {
      // No usable record. Create a personal dashboard with a placeholder name.
      // Do not open a Save-as dialog, because it stops the user.
      const definition = buildDashboardDefinition(
        searchParams,
        sections,
        nextCards,
        savedBase ?? undefined,
      );
      const metric = effectiveMetric(next);
      const create = (attempt: number) => {
        createDashboard.mutate(
          {
            name: generateUntitledDashboardName(metric),
            accessibility: "user",
            dashboard_definition: definition,
          },
          {
            onSuccess: (record) => {
              setIsAutoCreating(false);
              // commitDashboardSwitch does not call closeWidgetEditor, so clear
              // the id here.
              newWidgetIdRef.current = null;
              handleDashboardSaved(record);
              commitDashboardSwitch(record.dashboard_id, { reason: "saved" });
              showNotification(`Saved as "${record.name}".`, "success");
            },
            onError: (error) => {
              // A 409 means the owner already has this name. Try a new name.
              if (
                error instanceof RobotoConflictException &&
                attempt < MAX_AUTO_NAME_ATTEMPTS
              ) {
                create(attempt + 1);
                return;
              }
              setIsAutoCreating(false);
              showNotification(
                toastErrorMessage(
                  error,
                  "Failed to save dashboard. Please try again.",
                ),
                "error",
                ERROR_NOTIFICATION_DURATION_MS,
              );
            },
          },
        );
      };
      setIsAutoCreating(true);
      create(1);
      return;
    }

    // The guard above makes this unreachable. It narrows the type for the save.
    if (effectiveBaseRevision === null) {
      return;
    }

    saveDefinition(
      {
        dashboardId: effectiveDashboardId,
        definition: buildDashboardDefinition(
          searchParams,
          sections,
          nextCards,
          savedBase ?? undefined,
        ),
        baseRevision: effectiveBaseRevision,
      },
      closeWidgetEditor,
    );
  };

  /** The card the URL asks to reconfigure, or `null` in create mode and for a
   *  `:cardId` this dashboard has no card for. */
  const editorCard =
    editorMode?.kind === "edit"
      ? cards.find((card) => card.id === editorMode.cardId) ?? null
      : null;

  useEffect(() => {
    if (dashboardBodyRef.current !== null) {
      dashboardBodyRef.current.inert = editorMode !== null;
    }
  }, [editorMode]);

  /** What the editor starts from when the query says nothing about the
   *  widget: the card itself when reconfiguring one, a fresh chart in the
   *  first section when adding one. */
  const editorBase = ((): CardConfig | null => {
    if (editorMode === null) {
      return null;
    }
    return editorMode.kind === "edit"
      ? editorCard
      : defaultWidgetDraft({
          section: (sections[0] ?? fallbackSections()[0]).id,
        });
  })();

  /** The widget being configured, read off the URL. */
  const urlDraft =
    editorBase === null ? null : readWidgetDraft(searchParams, editorBase);

  /** Which section the widget joins: the draft's, or the first section when
   *  it names none this dashboard has. */
  const editorSection =
    sections.find((section) => section.id === urlDraft?.section) ??
    sections[0] ??
    fallbackSections()[0];

  // A new widget joins the section the heading names, even when the query
  // names a section this dashboard doesn't have.
  const editorDraft = ((): CardConfig | null => {
    if (urlDraft === null) {
      return null;
    }
    return editorMode?.kind === "create"
      ? { ...urlDraft, section: editorSection.id }
      : urlDraft;
  })();

  // An unknown `:cardId` returns to the dashboard with a notice. Gated on
  // `dashboardSeeded`: until the layout loads, EVERY card id is unknown.
  useEffect(() => {
    if (
      editorMode?.kind !== "edit" ||
      !dashboardSeeded ||
      editorCard !== null
    ) {
      return;
    }
    showNotification(
      "That widget is no longer on this dashboard.",
      "warning",
      ERROR_NOTIFICATION_DURATION_MS,
    );
    void navigate(
      {
        pathname: Goto.createMetricsDashboardRoute({
          deviceId,
          dashboardId: activeDashboardId,
        }),
        search: mergeParams(searchParams, clearWidgetDraft()).toString(),
      },
      { replace: true },
    );
  }, [
    editorMode,
    dashboardSeeded,
    editorCard,
    showNotification,
    navigate,
    deviceId,
    activeDashboardId,
    searchParams,
  ]);

  const { save: saveDefinition, isSaving: isSavingWidget } =
    useSaveDashboardDefinition({
      onSaved: handleDashboardSaved,
      onRevisionConflict: (serverRecord) =>
        setConflict({ serverRecord, fromSwitchGuard: false }),
    });

  const deviceIds = deviceId ? [deviceId] : undefined;
  const sessionIds = sessionId ? [sessionId] : undefined;

  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");
  const readyCollectionCustomFields = useReadyCustomFields({
    entityType: TargetEntityType.Collection,
    orgId,
    enabled: customFieldsEnabled,
  });
  const readyDeviceCustomFields = useReadyCustomFields({
    entityType: TargetEntityType.Device,
    orgId,
    enabled: customFieldsEnabled,
  });
  const readySessionCustomFields = useReadyCustomFields({
    entityType: TargetEntityType.Session,
    orgId,
    enabled: customFieldsEnabled,
  });
  const filterDefinitions = useMemo(
    () =>
      // Sorting the merged list interleaves the custom fields into their
      // entity's alphabetical block instead of trailing the natives.
      sortByFieldToken([
        ...DASHBOARD_PROPERTY_FILTERS,
        ...(customFieldsEnabled
          ? [
              ...buildDashboardCustomFieldFilterDefinitions(
                readyCollectionCustomFields.data ?? [],
                "collection",
              ),
              ...buildDashboardCustomFieldFilterDefinitions(
                readyDeviceCustomFields.data ?? [],
                "device",
              ),
              ...buildDashboardCustomFieldFilterDefinitions(
                readySessionCustomFields.data ?? [],
                "session",
              ),
            ]
          : []),
      ]),
    [
      customFieldsEnabled,
      readyCollectionCustomFields.data,
      readyDeviceCustomFields.data,
      readySessionCustomFields.data,
    ],
  );

  // The Group by picker's options: the same menu, narrowed to the fields a
  // group key can be one-to-one with a metric row. Derived here so a custom
  // field added to the org reaches both menus at once.
  const groupableFields = useMemo(
    () => groupableFieldDefinitions(filterDefinitions),
    [filterDefinitions],
  );

  // Reading the `filters` param is a JSON parse plus schema validation that
  // builds fresh objects — memoized so neither the work nor a new identity
  // lands on every render. The saved filters are the base a bare URL means;
  // a present param (an explicit empty included) overrides them.
  const { filters, matchMode } = useMemo(
    () => readFilterState(searchParams, savedBase?.filters),
    [searchParams, savedBase],
  );
  const activeFilters = useMemo<ActiveFiltersState>(
    () =>
      filters.length > 0
        ? { type: "structured", filters, matchMode }
        : { type: "none" },
    [filters, matchMode],
  );
  const condition = useMemo(
    () =>
      filters.length > 0
        ? filterToCondition(filters, { matchMode })
        : undefined,
    [filters, matchMode],
  );

  const applyFilterChange = (
    mutate: (prev: URLSearchParams) => URLSearchParams,
  ) => {
    setSearchParams(mutate, { replace: true });
    refreshDraftParams(
      buildDashboardDefinition(
        mutate(searchParams),
        sections,
        cards,
        savedBase ?? undefined,
      ),
    );
  };

  // Each mutator gets the saved filters as its base: editing on a bare URL
  // edits the SAVED set (an append extends it; removing the last chip leaves
  // the explicit empty override so the saved chips don't resurrect).
  const filterBase = savedBase?.filters;
  const handleFilterSelected = (output: FilterMenuOutput) => {
    // Structured-only here (no RoboQL), but the callback's union type still
    // forces this guard.
    if (output.type === "structured") {
      applyFilterChange((p) => appendFilter(p, output.filter, filterBase));
    }
  };
  const handleFilterRemove = (index: number) =>
    applyFilterChange((p) => removeFilterAt(p, index, filterBase));
  const handleFilterUpdate = (index: number, filter: Filter) =>
    applyFilterChange((p) => updateFilterAt(p, index, filter, filterBase));
  const handleFiltersClear = () =>
    applyFilterChange((p) => clearFilters(p, filterBase));
  const handleMatchModeChange = (updater: Updater<FilterMatchMode>) => {
    const nextMode =
      typeof updater === "function" ? updater(matchMode) : updater;
    applyFilterChange((p) => setFilterMatchMode(p, nextMode, filterBase));
  };

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load metrics: {error.message}</Alert>
    );
  }

  // The landing selection is still deciding what a bare route shows (waiting
  // on the dashboards list): keep the spinner up rather than flashing a
  // canvas that may immediately be replaced by the remembered/default
  // dashboard — the sample-seed invariant's visual half.
  if (selectionDeciding) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  // A selected dashboard whose record hasn't seeded the layout yet: render a
  // pending state instead of the editable canvas — until the record arrives
  // the layout state still holds the seed/previous content, which must never
  // masquerade as the requested dashboard. A load that can never succeed (a
  // hard-failed fetch or an invalid stored definition) is NOT gated here:
  // `effectiveDashboardId` is null for it, so it falls through to the normal
  // page + an error banner (below) rather than a full-page dead-end.
  if (effectiveDashboardId !== null && !dashboardSeeded) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!definitions || definitions.length === 0) {
    return <MetricsOnboarding />;
  }

  const allNames = definitions.map((d) => d.name);

  // Seed indices for unresolved cards (trends AND charts, one shared
  // counter): the i-th card without a valid metric falls back to the i-th
  // definition, so a fresh dashboard shows as many distinct metrics as the
  // org has instead of repeating the first one (wrapping when there are more
  // cards than definitions).
  const seedIndexById = new Map<string, number>();
  {
    let seedIndex = 0;
    for (const card of cards) {
      if (!(card.metric !== null && allNames.includes(card.metric))) {
        seedIndexById.set(card.id, seedIndex);
        seedIndex += 1;
      }
    }
  }

  /** The metric a card actually shows: its own, or its resolved fallback. */
  const effectiveMetric = (card: CardConfig): string =>
    card.kind === "trend"
      ? effectiveTrendMetric(card, allNames, seedIndexById.get(card.id) ?? 0)
      : effectiveChartMetric(card, allNames, seedIndexById.get(card.id) ?? 0);

  const renderCardBody = (card: CardConfig) => {
    if (card.kind === "trend") {
      return (
        <TrendTile
          name={effectiveMetric(card)}
          aggregation={card.aggregation}
          orgId={orgId}
          period={period}
          range={range}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          condition={condition}
        />
      );
    }
    return (
      <MetricChartCard
        definitions={definitions}
        config={card}
        seedIndex={seedIndexById.get(card.id) ?? 0}
        orgId={orgId}
        period={period}
        range={range}
        axisDomain={axisDomain}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
        condition={condition}
      />
    );
  };

  return (
    <div className={styles.page}>
      {/* A load that can never succeed — a hard-failed fetch (e.g. a
          non-existent dashboard id in the path) or a record whose stored
          definition is invalid — surfaces here as a banner over the normal page (the
          default/empty dashboard and the picker), instead of a full-page
          dead-end. The message reflects which failure occurred. */}
      {dashboardLoadFailed && (
        <Alert
          severity="error"
          action={
            // Only the invalid-definition case has a real record to delete; a
            // hard-failed fetch (e.g. a 404 id) has nothing to act on. The
            // dashboard is already broken and unloadable, so we delete directly
            // (no confirm) with pending/error feedback rather than gating a
            // known-broken record behind a dialog.
            loadedDefinitionInvalid ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                aria-label="Delete dashboard"
                onClick={handleDeleteFailedDashboard}
                disabled={deleteMutation.isPending}
                loading={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            ) : undefined
          }
        >
          Failed to load dashboard:{" "}
          {loadedDefinitionInvalid
            ? "its saved definition is not a valid dashboard layout."
            : dashboardQuery.error?.message}
        </Alert>
      )}
      {/* Neither unmounted (that would drop the grid's measurements and every
          card's in-flight query) nor hidden (the scrim dims it as context)
          while the editor is open, but `inert` — set on the node, since
          React 18 has no prop for it — so only the editor is reachable. */}
      <div
        className={styles.body}
        ref={dashboardBodyRef}
        data-testid="dashboard-canvas"
      >
        <DashboardHeader
          range={mode}
          period={period}
          onRangeChange={handleRangeChange}
          onPeriodChange={handlePeriodChange}
          filter={{
            definitions: filterDefinitions,
            onFilterSelected: handleFilterSelected,
          }}
          menu={{
            activeDashboardId: effectiveDashboardId,
            saveAsOpen,
            onSaveAsOpenChange: setSaveAsOpen,
            isDirty,
            currentDefinition,
            baseRevision: effectiveBaseRevision,
            onSelect: handleDashboardMenu,
            onSaved: handleDashboardSaved,
            onRevisionConflict: (serverRecord) =>
              setConflict({ serverRecord, fromSwitchGuard: false }),
            onRestore: handleDashboardRestore,
            // The unsaved view is named for the sample seed whenever it holds
            // one — asked for from the picker, or seeded by the priority-3
            // landing; an empty in-progress dashboard keeps the generic
            // placeholder.
            unsavedLabel:
              effectiveDashboardId === null && unsavedSeed === "sample"
                ? "Sample dashboard"
                : undefined,
          }}
        />
        <ResourceView.ActiveFilters
          filters={activeFilters}
          filterDefinitions={filterDefinitions}
          onFilterSelected={handleFilterSelected}
          onRemove={handleFilterRemove}
          onUpdate={handleFilterUpdate}
          onClear={handleFiltersClear}
          onMatchModeChange={handleMatchModeChange}
          metrics={false}
        />
        <DashboardSections
          sections={sections}
          cards={cards}
          onLayoutChange={handleLayoutChange}
          renderCardBody={renderCardBody}
          onEditCard={(card) => openWidgetEditor({ card })}
          onAddCard={(kind, section, placement) =>
            openWidgetEditor({ kind, section, placement })
          }
        />
      </div>
      {/* Edit mode waits for its card, which is unknown until the layout
          seeds. */}
      {editorMode !== null && editorDraft !== null && (
        <WidgetEditor
          // Remount per widget, so the fields' remembered aggregation doesn't
          // carry across.
          key={editorMode.kind === "edit" ? editorMode.cardId : NEW_WIDGET_ID}
          mode={editorMode}
          draft={editorDraft}
          onDraftChange={updateWidgetDraft}
          section={editorSection}
          allNames={allNames}
          groupableFields={groupableFields}
          resolveMetric={effectiveMetric}
          renderPreview={renderCardBody}
          onCommit={commitWidget}
          // Disable commit during a save or during all auto-create attempts, so
          // an auto-create cannot occur two times.
          isSaving={isSavingWidget || isAutoCreating}
          onClose={closeWidgetEditor}
        />
      )}
      {/* Asks what to do with unsaved layout edits when the user picks a
          DIFFERENT dashboard — the one moment a working copy is about to be
          replaced. Everything else is silent: the draft persists and
          auto-loads, so leaving the page loses nothing. Gated on there being
          no conflict so the conflict dialog, which owns its own resolution,
          is never stacked under this one. */}
      {pendingSwitch !== null &&
        pendingSwitch.key === draftKey &&
        conflict === null && (
          <DashboardSwitchGuard
            dashboard={loadedDashboard}
            currentDefinition={currentDefinition}
            baseRevision={effectiveBaseRevision}
            onKeepEditing={() => setPendingSwitch(null)}
            onDiscard={() => dropDraft(draftKey)}
            onSaved={handleDashboardSaved}
            onRevisionConflict={(serverRecord) =>
              setConflict({ serverRecord, fromSwitchGuard: true })
            }
            onProceed={() => {
              setPendingSwitch(null);
              commitDashboardSwitch(pendingSwitch.nextId, pendingSwitch.meta);
            }}
            onSaveAs={() => {
              setPendingSwitch(null);
              setSaveAsOpen(true);
            }}
          />
        )}
      {conflict !== null && (
        <DashboardConflictDialog
          serverRecord={conflict.serverRecord}
          currentDefinition={currentDefinition}
          onDiscardAndReload={handleConflictDiscardAndReload}
          onSavedCopy={handleConflictSavedCopy}
        />
      )}
    </div>
  );
}
